import {
  EnumOwnerType,
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  useTagGroupManyQuery,
  useTagManyQuery,
} from "gql/generated";
import { useParams } from "react-router-dom";

export const useGetAllOrganizationTags = () => {
  const { organizationId } = useParams();

  // Query all the different tag groups that are needed
  // Back office tags & tagGroups are owned by the organization and need to
  // be fetched separately
  const {
    data: backOfficeTagGroups,
    loading: backOfficeTagGroupsLoading,
    error: backOfficeTagGroupsError,
  } = useTagGroupManyQuery({
    variables: {
      filter: {
        type: [EnumTagGroupType.OrganizationSpecific],
        key: EnumTagGroupKey.BackOffice,
      },
    },
  });
  const {
    data: unownedTagGroups,
    loading: unownedTagGroupsLoading,
    error: unownedTagGroupsError,
  } = useTagGroupManyQuery({
    variables: {
      filter: {
        type: [
          EnumTagGroupType.BusinessType,
          EnumTagGroupType.Global,
          EnumTagGroupType.Amenity,
        ],
      },
    },
  });

  // Back office tags are tags that are owned by the organization
  const {
    data: backOfficeTags,
    loading: backOfficeTagsLoading,
    error: backOfficeTagsError,
  } = useTagManyQuery({
    variables: {
      filter: {
        tagGroup: backOfficeTagGroups
          ? backOfficeTagGroups.tagGroupMany.map((tagGroup) => tagGroup._id)
          : [],
        owner: { entityId: organizationId, type: EnumOwnerType.Organization },
        entityTypes: [EnumTagEntityTypes.Operator],
      },
      limit: 10000,
    },
  });

  // Amenities, global keywords and businessType tags are not owned by the org
  // so this query can run for them all together
  const {
    data: unownedTags,
    loading: unownedTagsLoading,
    error: unownedTagsError,
  } = useTagManyQuery({
    variables: {
      filter: {
        tagGroup: unownedTagGroups
          ? unownedTagGroups.tagGroupMany
              // For Amenities, filter out all the tag groups that are not
              // Roofed Accommodation ones for now. This will be changed when
              // we begin supporting more amenity types in the future
              .filter((tagGroup) => {
                if (tagGroup.type === EnumTagGroupType.Amenity) {
                  return tagGroup.key === EnumTagGroupKey.RoofedAccommodation;
                }
                return true;
              })
              .map((tagGroup) => tagGroup._id)
          : [],
        entityTypes: [EnumTagEntityTypes.Operator],
      },
      limit: 10000,
    },
  });

  // Combine all the tags into one variable
  const tags = (backOfficeTags?.tagMany ? backOfficeTags.tagMany : []).concat(
    unownedTags?.tagMany ? unownedTags.tagMany : [],
  );

  return {
    tags,
    loading:
      backOfficeTagsLoading ||
      unownedTagsLoading ||
      backOfficeTagGroupsLoading ||
      unownedTagGroupsLoading,
    error:
      backOfficeTagsError ||
      unownedTagsError ||
      backOfficeTagGroupsError ||
      unownedTagGroupsError,
  };
};
